import { Bible_fhlwh_json } from "./Bible_fhlwh_json.es2023.js";
import { Bible_bhs_json } from "./Bible_bhs_json.es2023.js";
import { Bible_lxx_json } from "./Bible_lxx_json.es2023.js";
import { BibleConstantHelper } from "./BibleConstantHelper.es2023.js";
import { getAjaxUrl } from "./getAjaxUrl.es2023.js";
import { isRDLocation } from "./isRDLocation.es2023.js";
import { TPPageState } from "./TPPageState.es2023.js";
import { qsb } from "./api/qsb.js";
import { BookRegistry } from "./BookRegistry.es2023.js";
import { qafAsync } from "./qafAsync.es2023.js";
import { qsubAsync, apoVersionName } from "./qsubAsync.es2023.js";
import { lectureVersions } from "./lectureVersions.es2023.js";

// renderLectureHtml @ FhlLecture.js
/**
 * @returns {Promise<TpResultBibleText[]>}
 */
export async function lecture_get_data_async() {
    /** @type {TPPageState} */
    const ps = TPPageState.s
    const book = ps.bookIndex
    const chap = ps.chap
    const gb = ps.gb

    // 使徒教父：不是譯本，只有一種文本 (qaf.php)，不管 ps.version 選了什麼
    if (BookRegistry.corpusOf(book) === 'af') {
        return [await qafAsync({ bid: book, chap })]
    }
    // 次經 (qsub.php)：使用者選的次經文本 (c1933、chdc，並排；沒選就 c1933)，不並排新舊約的譯本。簡繁由上游 gb=1 處理。
    // 某個文本取不到 (網路、上游錯誤) 只讓那一欄空白並註明，不影響另一欄；全部都失敗才算失敗
    if (BookRegistry.corpusOf(book) === 'apo') {
        const versions = lectureVersions(ps)
        const settled = await Promise.allSettled(versions.map(version => qsubAsync({ bid: book, chap, version, gb })))
        if (settled.every(r => r.status === 'rejected')) throw settled[0].reason
        return settled.map((r, i) => r.status === 'fulfilled'
            ? { ...r.value, version: versions[i], missingChap: r.value.record.length === 0 } // 該文本沒有這一章：欄是空的，標題註明
            : { status: 'success', version: versions[i], v_name: apoVersionName(versions[i], gb == 1), record_count: 0, record: [], prev: null, next: null, failedMsg: String(r.reason?.message ?? r.reason) })
    }

    const versionPromises = lectureVersions(ps).map(ver => {
        if (ver == "fhlwh") {
            return get_orig_async(Bible_fhlwh_json.s, "fhlwh", "新約原文", book, chap)
        } else if (ver == "bhs") {
            return get_orig_async(Bible_bhs_json.s, "bhs", "舊約馬索拉原文", book, chap)
        } else if (ver == "lxx" && book <= 39) {
            return get_orig_async(Bible_lxx_json.s, "lxx", "七十士譯本", book, chap)
        } else {
            return get_from_qsb_php_async(book, chap, gb, ver)
        }
    })

    // - 同步取得
    const joResults = await Promise.all(versionPromises)

    // - 將 engs 或 chineses 轉成 book
    joResults.map(a1 => add_book_property_to_bibletext_record(a1))

    return joResults
}

/**
 * @param {TpOneRecordBibleText} result 
 * @param {boolean} is_remove_engs_and_chineses 
 */
function add_book_property_to_bibletext_record(result, is_remove_engs_and_chineses = false) {
    // 找出所有用到的 engs. 類似 np.unique
    let fnEngs2 = a1 => a1.engs != null ? a1.engs : a1.chineses
    let fnEngs3 = a1 => a1 != null ? fnEngs2(a1) : null

    let allengs = result.record.map(fnEngs2)

    // 將 next 與 prev 的也加入
    allengs.push(fnEngs3(result.next), fnEngs3(result.prev))

    // 型成 engs: book 的對應表，順便就有 Set 效果
    let dict_chap_cnt = {}
    for (let i = 0; i < allengs.length; i++) {
        const engs = allengs[i];

        if (engs != null && dict_chap_cnt[engs] == undefined) {
            dict_chap_cnt[engs] = BibleConstantHelper.getBookId(engs.toLowerCase())
        }
    }

    // 開始處理每個 record
    for (const a1 of result.record) {
        // 有資料，原本就有 book 就略過
        if (a1.book == undefined) {
            // a1 是一個 record
            // 取得 engs，然後從 dict_chap_cnt 中取得 book
            const keyEngs = fnEngs2(a1)
            let book = dict_chap_cnt[keyEngs]
            if (book == undefined) {
                // 若沒有找到，則預設為 0
                console.error(`找不到 book 對應的 engs: ${a1.engs || a1.chineses}`)
                book = 0
            }

            // 設定 book 屬性
            a1.book = book
        }

        if (is_remove_engs_and_chineses) {
            // 若要移除 engs 與 chineses
            delete a1.engs
            delete a1.chineses
        }
    }

    // 處理 next 與 prev
    if (result.next != undefined) {
        if (result.next.book == undefined) {
            // next 也要加上 book 屬性
            const keyEngs = fnEngs3(result.next)
            result.next.book = dict_chap_cnt[keyEngs]
            if (result.next.book == undefined) {
                console.error(`找不到 next book 對應的 engs: ${result.next}`)
                result.next.book = 0
            }
        }
        if (is_remove_engs_and_chineses) {
            delete result.next.engs
            delete result.next.chineses
        }
    }
    if (result.prev != undefined) {
        if (result.prev.book == undefined) {
            // prev 也要加上 book 屬性
            const keyEngs = fnEngs3(result.prev)
            result.prev.book = dict_chap_cnt[keyEngs]
            if (result.prev.book == undefined) {
                console.error(`找不到 prev book 對應的 engs: ${result.prev}`)
                result.prev.book = 0
            }
        }
        if (is_remove_engs_and_chineses) {
            delete result.prev.engs
            delete result.prev.chineses
        }
    }
}

/**
 * 新舊約原文、七十士譯本，讀本機嵌 SN 的資料 (tools/gen_bible_orig.mjs、gen_bible_lxx.mjs 產生)，不打 qsb.php
 * @param {Bible_fhlwh_json|Bible_bhs_json|Bible_lxx_json} json
 * @param {string} version fhlwh bhs lxx
 * @param {string} v_name
 */
async function get_orig_async(json, version, v_name, book, chap) {
    await json.loadAsync(); // 新約約 620KB、舊約約 1.4MB、七十士約 1.7MB，只有顯示此譯本時才載入

    // where [0]=bk and [1]=ch
    const jaBible = (json.filecontent?.["data"] ?? []).filter(ja => ja[0] == book && ja[1] == chap)

    // chap, sec, bible_text
    const jaBible2 = jaBible.map(ja => ({
        chap: ja[1],
        sec: ja[2],
        bible_text: ja[3],
        book: ja[0]
    }))
    const joResult = {
        "status": "success",
        "version": version,
        "record": jaBible2,
        "record_count": jaBible2.length,
        "v_name": v_name
    }
    return joResult
}

async function get_from_qsb_php_async(book, chap, gb, version){
    // - 產生 qstr
    const bookName = BibleConstantHelper.getBookNameArrayChineseShort()[book - 1]
    const qstr = `${bookName}${chap}`
    const qsbParams = { qstr: qstr, ver: version, isGb: gb }

    // qsb.php 不回傳譯本名稱，要查 abvphp。快照 (abvphp_snapshot.js) 裡有就不用等；沒有的 (FHL 新加的譯本) 等 uiabv.php
    const isgb = gb == 1
    const [joResult] = await Promise.all([qsb(qsbParams), abvphp.readyAsync([version], isgb)])

    // qb 會回傳 prev next, 但 qsb 不會，所以要自己產生
    // joResult.prev = {book:1,chap:2,sec:1}
    // joResult.next = {book:1,chap:4,sec:1}
    const v_name = abvphp.get_cname_from_book(version, isgb) || (version == 'unv' ? 'FHL和合本' : version)
    joResult.v_name = v_name
    joResult.version = version

    return joResult
}